import styles from "./Account.module.scss";
import { Link, useNavigate } from "react-router-dom";
import useAppSelector from "../../../hooks/useAppSelector";
import EmptyAccount from "./EmptyAccount/EmptyAccount";
import { useTranslation } from "react-i18next";
import subscriptionImage from "../../../assets/images/pictures/subscription.jpg";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import dayjs from "dayjs";
import { setUserSubscriptionToInitial } from "../../../store/userSubscription/userSubscriptionSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { logout } from "../../../store/userProfile/userProfileSlice";
import { setPaymentMethodsToInitial } from "../../../store/userPaymentMethods/userPaymentMethodsSlice";
import { setLocationToInitial } from "../../../store/userLocation/userLocationSlice";
import { AuthService } from "../../../services/auth.service";
import { useGetLoyaltyPointBalanceQuery } from "../../../api/queries";
import { useUserMutations } from "../../../api/mutations";
import { useGetUserSubscriptionQuery } from "../../../api/queries/useGetUserSubscriptionQuery";

const Account = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { data: loyaltyBalance } = useGetLoyaltyPointBalanceQuery(user?.id || null);
  const { deleteUser } = useUserMutations();
  const { data: userSubscription } = useGetUserSubscriptionQuery(user?.id || null);

  const clickOnManagePlan = () => {
    navigate("/settings/account/manage-plan");
  };

  const totalLeftSubscriptionCredits = userSubscription
    ? userSubscription.credits - userSubscription.usageCredits
    : 0;
  const totalCreditsForWallet =
    (loyaltyBalance ? loyaltyBalance.loyaltyPointsBalance : 0) + totalLeftSubscriptionCredits;

  const deleteAccount = async () => {
    deleteUser(
      {},
      {
        onSuccess: () => {
          onLogout();
        },
      }
    );
  };

  const onLogout = () => {
    dispatch(logout());
    dispatch(setPaymentMethodsToInitial());
    dispatch(setLocationToInitial());
    dispatch(setUserSubscriptionToInitial());
    AuthService.logout();
    navigate("/login");
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t("Account")}</h2>
      <div className={styles.wrapper}>
        <div className={styles.box}>
          {userSubscription ? (
            <>
              <div className={styles.topRow}>
                <img src={subscriptionImage} alt={"subscription"} />
                <div>
                  <h4 className={styles.subtitle}>
                    {totalLeftSubscriptionCredits} {t("CreditLeft")}
                  </h4>
                  <p className={styles.text}>
                    {t("Total")} {userSubscription.credits} {t("credit")}
                  </p>
                  {userSubscription.cancelAt && (
                    <p className={styles.text}>
                      {t("Will be cancel")} {dayjs(userSubscription.cancelAt).format("MMMM D")}
                    </p>
                  )}
                </div>
              </div>
              <div className={styles.descriptionBox}>
                <p className={styles.text}>{t("YourCurrentPlanIs")}</p>
                <h4 className={styles.subtitle}>
                  {userSubscription.subscription.credits} {t("CreditPlan")}
                </h4>
                <p className={styles.text}>
                  €{userSubscription.subscription.price.toFixed(2)}/mo - {t("Ends")}{" "}
                  {dayjs(userSubscription.nextPaymentDate).format("MMMM D")}
                </p>
              </div>
              <p className={styles.annotation}>
                {t("YouDoNotHaveAnyCreditsThatCanRollOverToYourNewPlan")}{" "}
                <Link className={styles.link} to={"/"}>
                  {t("LearnMore")}
                </Link>
              </p>
              <div className={styles.btnWrapper}>
                <CustomButton
                  text={t("ManagePlan")}
                  type={"button"}
                  typeStyle={"fill"}
                  functionOnClick={clickOnManagePlan}
                />
              </div>
            </>
          ) : (
            <EmptyAccount />
          )}
        </div>
        <div className={`${styles.box} ${styles.balance}`}>
          <div className={styles.topWrapper}>
            <h4 className={styles.subtitle}>{t("AccountBalance")}</h4>
            <div className={styles.balanceRow}>
              <p className={styles.text}>{t("LoyaltyRewards")}</p>
              <p className={styles.text}>
                {loyaltyBalance ? loyaltyBalance.loyaltyPointsBalance : 0} {t("Credits")}
              </p>
            </div>
          </div>

          <div className={styles.border}></div>
          <div className={`${styles.balanceRow} ${styles.total}`}>
            <h4 className={styles.subtitle}>{t("Total")}</h4>
            <h4 className={styles.subtitle}>
              {totalCreditsForWallet} {t("Credits")}
            </h4>
          </div>
          <div onClick={() => navigate("/settings/refer-friends")} className={styles.bottom}>
            <p className={styles.text}>{t("ReferAndWinWithClubPass")}</p>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.95019 4.08004L15.4702 10.6C16.2402 11.37 16.2402 12.63 15.4702 13.4L8.9502 19.92"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      <p className={styles.text}>
        {t("HaveAnyQuestions")}{" "}
        <Link className={styles.link} to={"/"}>
          {t("ReachOutToUs")}
        </Link>
      </p>

      <div className={styles.deleteAccount}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M11.9 1.66687C12.2498 1.66696 12.5907 1.77711 12.8744 1.98171C13.1581 2.18631 13.3703 2.475 13.4808 2.80687L13.9333 4.16687H16.6667C16.8877 4.16687 17.0996 4.25467 17.2559 4.41095C17.4122 4.56723 17.5 4.77919 17.5 5.0002C17.5 5.22122 17.4122 5.43318 17.2559 5.58946C17.0996 5.74574 16.8877 5.83354 16.6667 5.83354L16.6642 5.8927L15.9417 16.0119C15.8966 16.6424 15.6143 17.2324 15.1517 17.6632C14.6891 18.0939 14.0805 18.3335 13.4483 18.3335H6.55167C5.91955 18.3335 5.31092 18.0939 4.84831 17.6632C4.38569 17.2324 4.10342 16.6424 4.05833 16.0119L3.33583 5.89187C3.33433 5.87246 3.33349 5.853 3.33333 5.83354C3.11232 5.83354 2.90036 5.74574 2.74408 5.58946C2.5878 5.43318 2.5 5.22122 2.5 5.0002C2.5 4.77919 2.5878 4.56723 2.74408 4.41095C2.90036 4.25467 3.11232 4.16687 3.33333 4.16687H6.06667L6.51917 2.80687C6.62975 2.47486 6.84203 2.18608 7.12592 1.98147C7.4098 1.77685 7.75089 1.66679 8.10083 1.66687H11.9ZM14.9975 5.83354H5.0025L5.72083 15.8927C5.73579 16.1029 5.82981 16.2996 5.98397 16.4432C6.13812 16.5869 6.34096 16.6668 6.55167 16.6669H13.4483C13.659 16.6668 13.8619 16.5869 14.016 16.4432C14.1702 16.2996 14.2642 16.1029 14.2792 15.8927L14.9975 5.83354ZM8.33333 8.33354C8.53744 8.33356 8.73445 8.4085 8.88698 8.54413C9.03951 8.67977 9.13695 8.86666 9.16083 9.06937L9.16667 9.16687V13.3335C9.16643 13.5459 9.0851 13.7502 8.93929 13.9047C8.79349 14.0591 8.59421 14.1521 8.38217 14.1645C8.17014 14.177 7.96135 14.108 7.79847 13.9716C7.6356 13.8353 7.53092 13.6419 7.50583 13.431L7.5 13.3335V9.16687C7.5 8.94586 7.5878 8.73389 7.74408 8.57761C7.90036 8.42133 8.11232 8.33354 8.33333 8.33354ZM11.6667 8.33354C11.8877 8.33354 12.0996 8.42133 12.2559 8.57761C12.4122 8.73389 12.5 8.94586 12.5 9.16687V13.3335C12.5 13.5546 12.4122 13.7665 12.2559 13.9228C12.0996 14.0791 11.8877 14.1669 11.6667 14.1669C11.4457 14.1669 11.2337 14.0791 11.0774 13.9228C10.9211 13.7665 10.8333 13.5546 10.8333 13.3335V9.16687C10.8333 8.94586 10.9211 8.73389 11.0774 8.57761C11.2337 8.42133 11.4457 8.33354 11.6667 8.33354ZM11.9 3.33354H8.1L7.8225 4.16687H12.1775L11.9 3.33354Z"
            fill="#D93131"
          />
        </svg>

        <p className={styles.deleteAccountText} onClick={deleteAccount}>
          {t("DeleteMyAccount")}
        </p>
      </div>
    </div>
  );
};

export default Account;
